import { useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  AlertTriangle, CalendarClock, FileText, Users, Wrench, Building2, Euro, Receipt, Wallet,
  UserPlus, Target, FileSpreadsheet, TrendingUp, ShoppingBag, GraduationCap,
} from "lucide-react";

const COLORS = ["#EA580C", "#2563EB", "#16A34A"];
const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const Kpi = ({ label, value, icon: Icon, tone = "default", testid }) => (
  <Card
    data-testid={testid}
    className="p-5 rounded-sm border-border shadow-none hover:-translate-y-1 hover:shadow-lg transition-transform duration-200"
  >
    <div className="flex items-start justify-between">
      <p className="overline">{label}</p>
      <Icon className={`h-4 w-4 ${tone === "alert" ? "text-destructive" : "text-primary"}`} />
    </div>
    <p className={`font-display text-4xl font-black tracking-tighter mt-3 ${tone === "alert" ? "text-destructive" : ""}`}>
      {value}
    </p>
  </Card>
);

const AgentDashboard = ({ s }) => {
  const chartData = (s.direct_sales_year_chart || []).map((m, i) => ({ ...m, label: MONTH_LABELS[i] }));
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Panoramica commerciale" subtitle="Dashboard" />

      <Card className="p-6 rounded-sm shadow-none mb-8" data-testid="chart-vendite-anno">
        <p className="overline">Vendite dirette {new Date().getFullYear()}</p>
        <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-6">Valore per mese</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#71717a" />
              <YAxis tick={{ fontSize: 11 }} stroke="#71717a" />
              <Tooltip formatter={(v) => eur(v)} />
              <Bar dataKey="value" fill="#EA580C" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <Kpi label="Clienti gestiti" value={s.customers} icon={Users} testid="kpi-clienti" />
        <Kpi label="Nuovi clienti nel mese" value={s.new_customers_month} icon={UserPlus} testid="kpi-nuovi-clienti" />
        <Kpi label="Lead attivi" value={s.leads_active} icon={Target} testid="kpi-lead-attivi" />
        <Kpi label="Valore pipeline lead" value={eur(s.leads_active_value)} icon={Euro} testid="kpi-lead-valore" />
        <Kpi label="Preventivi in attesa" value={s.quotes_pending} icon={FileSpreadsheet} testid="kpi-preventivi-attesa" />
        <Kpi label="Valore preventivi in attesa" value={eur(s.quotes_pending_value)} icon={Euro} testid="kpi-preventivi-valore" />
        <Kpi label="Conversione preventivi (mese)" value={`${s.quotes_conversion_rate_month}%`}
             icon={TrendingUp} testid="kpi-conversione" />
        <Kpi label="Vendite dirette nel mese" value={eur(s.direct_sales_month_value)}
             icon={ShoppingBag} testid="kpi-vendite-mese" />
        <Kpi label="Iscrizioni da confermare" value={s.enrollments_pending}
             icon={GraduationCap} tone={s.enrollments_pending > 0 ? "alert" : "default"} testid="kpi-iscrizioni" />
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [interventions, setInterventions] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setS(data));
    if (user?.role !== "agente") {
      api.get("/interventions").then(({ data }) => setInterventions(data.slice(0, 8)));
    }
  }, [user?.role]);

  if (!s) return <p className="overline">Caricamento…</p>;

  if (user?.role === "agente") return <AgentDashboard s={s} />;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Panoramica operativa" subtitle="Dashboard" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Kpi label="Clienti" value={s.customers} icon={Users} testid="kpi-clienti" />
        <Kpi label="Impianti" value={s.plants} icon={Building2} testid="kpi-impianti" />
        <Kpi label="Interventi aperti" value={s.interventions_open} icon={Wrench} testid="kpi-interventi-aperti" />
        <Kpi label="Oggi in agenda" value={s.interventions_today} icon={CalendarClock} testid="kpi-oggi" />
        <Kpi label="Manut. scadute" value={s.maintenance_overdue} icon={AlertTriangle} tone="alert" testid="kpi-scadute" />
        <Kpi label="Manut. 30 gg" value={s.maintenance_next30} icon={CalendarClock} testid="kpi-prossime" />
        <Kpi label="Contratti attivi" value={s.contracts_active} icon={FileText} testid="kpi-contratti" />
        <Kpi label="Ricavi mese" value={eur(s.revenue_month)} icon={Euro} testid="kpi-ricavi" />
        <Kpi label="Valore contratti attivi" value={eur(s.contracts_value)} icon={FileText} testid="kpi-contratti-valore" />
        <Kpi label="Da fatturare" value={eur(s.to_invoice)} icon={Receipt} testid="kpi-da-fatturare" />
        <Kpi label="Fatturato non incassato" value={eur(s.invoiced_unpaid)} icon={Wallet}
             tone={s.invoiced_unpaid > 0 ? "alert" : "default"} testid="kpi-non-incassato" />
        <Kpi label="Incassato nel mese" value={eur(s.collected_month)} icon={Euro} testid="kpi-incassato-mese" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Card className="lg:col-span-2 p-6 rounded-sm shadow-none" data-testid="chart-interventi">
          <p className="overline">Interventi per mese</p>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-6">Andamento attività</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.chart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 11 }} stroke="#71717a" />
                <Tooltip />
                <Bar dataKey="interventi" fill="#EA580C" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-sm shadow-none" data-testid="chart-tipologie">
          <p className="overline">Tipologie</p>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1">Mix interventi</h3>
          <div className="h-52 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={s.by_type} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                  {s.by_type.map((e, i) => (
                    <Cell key={e.name} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {s.by_type.map((t, i) => (
              <div key={t.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 capitalize">
                  <span className="h-2 w-2" style={{ background: COLORS[i % COLORS.length] }} />
                  {t.name}
                </span>
                <span className="font-mono font-semibold">{t.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="mt-8 rounded-sm shadow-none overflow-hidden" data-testid="ultimi-interventi">
        <div className="p-6 border-b border-border">
          <p className="overline">Attività recenti</p>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1">Ultimi interventi</h3>
        </div>
        <div className="divide-y divide-border">
          {interventions.map((i) => (
            <div key={i.id} className="px-6 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{i.customer_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  <span className="font-mono">{i.code}</span> · {i.type} · {i.technician_name || "non assegnato"}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-mono">{eur(i.totals?.total)}</span>
                <Badge variant={i.status === "completato" ? "secondary" : "default"} className="rounded-sm">
                  {i.status}
                </Badge>
              </div>
            </div>
          ))}
          {interventions.length === 0 && <p className="p-6 text-sm text-muted-foreground">Nessun intervento.</p>}
        </div>
      </Card>
    </div>
  );
}
